import React, { useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { COOKIE_CONSENT_KEY } from '@fedsim/constants'
import {
  resetBrands,
  resetSegments,
  resetFeuds,
  resetGame,
  resetReigns,
  resetChampionships,
  resetShows,
  resetWrestlers,
  resetProductions,
  resetDrafts,
  resetAppearances,
  resetArenas,
  resetAll
} from '@fedsim/actions'
import Cookies from 'js-cookie'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Grid,
  HeaderSecondary,
  SpacedDivider,
  OverflowWrapper,
  Spacer,
  SpacedPaper,
} from '@fedsim/components'
import { NOOP } from '@fedsim/helpers'
import { HeaderTertiary } from '@fedsim/components'

import { routesList } from 'constants/routes'
import { ManageAllTutorialAlerts } from "components/tutorial-alert"
import ConfirmButton from 'components/confirm-button'
import TitleBar from 'components/title'

import { getEntityTotalsTotals } from 'selectors'
import { trackEv, Categories, Actions } from 'analytics'

import Saves from './saves';
import Game from "./game"

const Item = ({ total = null, label = 'Label', onClear = NOOP }) => {
  return (
    <SpacedPaper
      sx={{
        color: 'inherit',
        backgroundColor: 'background.paper',
        alignItems: 'center',
        boxShadow: '5px 5px lightgray',
        display: 'flex',
        height: "auto",
        flexDirection: 'row',
        px: 1,
        py: 1,
        justifyContent: 'space-between',
      }}
    >
      <HeaderSecondary sx={{ fontSize: 20 }} tabIndex={0}>{label}</HeaderSecondary>
      {total > 0 && (<Box>
        <ConfirmButton label={`Delete ${total}?`} confirmLabel='Confirm Delete?' onClick={onClear} />
      </Box>)}
    </SpacedPaper>
  )
}

function SettingsReset() {
  const dispatch = useDispatch()
  const totals = useSelector(getEntityTotalsTotals)
  const generateResetMessage = label => ({
    label,
    total: totals[label]
  })

  const onResetArenas = useCallback(() => {
    dispatch(resetArenas())

    trackEv(Categories.Settings, Actions.Reset, { value: 0, label: "Arena" })
  }, [dispatch])
  const onResetAppearances = useCallback(() => {
    dispatch(resetAppearances())

    trackEv(Categories.Settings, Actions.Reset, { value: 0, label: "Appearance" })
  }, [dispatch])

  const onResetBrands = useCallback(() => {
    dispatch(resetBrands())

    trackEv(Categories.Settings, Actions.Reset, { value: 1, label: "Brand" })
  }, [dispatch])
  const onResetChampionships = useCallback(() => {
    dispatch(resetChampionships())

    trackEv(Categories.Settings, Actions.Reset, { value: 2, label: "Championship" })
  }, [dispatch])

  const onResetFeuds = useCallback(() => {
    dispatch(resetFeuds())

    trackEv(Categories.Settings, Actions.Reset, { value: 3, label: "Feud" })
  }, [dispatch])
  const onResetProductions = useCallback(() => {

    dispatch(resetProductions())

    trackEv(Categories.Settings, Actions.Reset, { value: 4, label: "Production" })
  }, [dispatch])

  const onResetReigns = useCallback(() => {
    dispatch(resetReigns())

    trackEv(Categories.Settings, Actions.Reset, { value: 5, label: "Reign" })
  }, [dispatch])

  const onResetShows = useCallback(() => {
    dispatch(resetShows())

    trackEv(Categories.Settings, Actions.Reset, { value: 6, label: "Show" })
  }, [dispatch])

  const onResetDrafts = useCallback(() => {
    dispatch(resetDrafts())

    trackEv(Categories.Settings, Actions.Reset, { value: 7, label: "Draft" })
  }, [dispatch])

  const onResetWrestlers = useCallback(() => {
    dispatch(resetWrestlers())

    trackEv(Categories.Settings, Actions.Reset, { value: 8, label: "Wrestler" })
  }, [dispatch])

  const onResetSegments = useCallback(() => {
    dispatch(resetSegments())

    trackEv(Categories.Settings, Actions.Reset, { value: 9, label: "Segment" })
  }, [dispatch])

  const onResetGame = useCallback(() => {
    dispatch(resetGame())

    trackEv(Categories.Settings, Actions.Reset, { value: 10, label: "Game" })
  }, [dispatch])

  const actions = [
    {
      ...generateResetMessage('Appearance'),
      onClear: onResetAppearances,
    },
    {
      ...generateResetMessage('Arena'),
      onClear: onResetArenas,
    }, {
      ...generateResetMessage('Brand'),
      onClear: onResetBrands,
    },
    {
      ...generateResetMessage('Game'),
      onClear: onResetGame
    },
    {
      ...generateResetMessage('Championship'),
      onClear: onResetChampionships
    },
    {
      ...generateResetMessage('Reign'),
      onClear: onResetReigns
    },
    {
      ...generateResetMessage('Feud'),
      onClear: onResetFeuds
    },
    {
      ...generateResetMessage('Wrestler'),
      onClear: onResetWrestlers
    },
    {
      ...generateResetMessage('Production'),
      onClear: onResetProductions
    },
    {
      ...generateResetMessage('Show'),
      onClear: onResetShows
    },
    {
      ...generateResetMessage('Segment'),
      onClear: onResetSegments
    },
    {
      ...generateResetMessage('Draft'),
      onClear: onResetDrafts
    }
  ].map(item => ({
    ...item,
    color: 'error',
  }))
  return (
    <>
      {actions.map((props) => (
        <Box key={props.label} mb={1}>
          <Item key={props.label} {...props} />
        </Box>
      ))}
    </>
  )
}

export function ResetAll() {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const onConfirm = useCallback(() => {
    try {
      localStorage.clear()

      Cookies.remove(COOKIE_CONSENT_KEY)

      trackEv(Categories.Settings, Actions.Reset, { value: 0, label: "All" })

      dispatch(resetAll())
      navigate(routesList.SETUP.path)
    } catch (error) {
      console.log(error)
    }
  }, [dispatch, navigate])

  return (
    <ConfirmButton label="Reset Everything?" confirmLabel='Confirm Delete' onClick={onConfirm} />
  )
}


export function ResetsWrapper() {
  return (
    <>
      <TitleBar title="Settings">
        <ResetAll />
      </TitleBar>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <HeaderTertiary>Data</HeaderTertiary>
          <SpacedDivider />
          <SettingsReset />
        </Grid>
        <Grid item xs={12} md={4}>
          <HeaderTertiary>Game Date</HeaderTertiary>
          <SpacedDivider />
          <Game />
          <Spacer />
          <HeaderTertiary>Tutorials</HeaderTertiary>
          <SpacedDivider />
          <OverflowWrapper><ManageAllTutorialAlerts /></OverflowWrapper>
        </Grid>
        <Grid item xs={12} md={4}>
          <HeaderTertiary>Save Games</HeaderTertiary>
          <SpacedDivider />
          <Saves />
        </Grid>
      </Grid>
    </>
  )
}

export default ResetsWrapper
